/**
 * Yield band form (machine settings row, F6-B1.c). uc-core LoadYieldBandDto
 * (src/modules/process/dto/yield-band.dto.ts) and YieldBandService.load:
 *   minPct, maxPct     /^\d{1,3}(\.\d{1,2})?$/, 0–100, min ≤ max
 *   minFlushKg?        /^\d{1,8}(\.\d{1,2})?$/
 *   minInputKg?        same
 *   effectiveFrom?     IST day, default today; never before the previous version's
 *   changeNote?        ≤ 200 chars, required from version 2 on
 * Bands are insert-only: saving for a crop, variety, step and machine that already has a band
 * adds the next version, which supersedes it from its effective date.
 */

export interface YieldBandDraft {
  cropId: string
  /** Empty = every variety. */
  cropVarietyId: string
  stepCode: string
  /** Empty = all machines (the generic band). */
  equipmentId: string
  minPct: string
  maxPct: string
  minFlushKg: string
  minInputKg: string
  effectiveFrom: string
  changeNote: string
}

export type YieldBandProblems = Partial<Record<keyof YieldBandDraft, string>>

/** The newest version already loaded for the same crop, variety, step and machine. */
export interface PreviousBand {
  version: number
  effectiveFrom: string
}

export interface YieldBandKey {
  cropId: string
  cropVarietyId: string | null
  stepCode: string
  equipmentId: string | null
}

export type YieldBandBody = {
  cropId: string
  cropVarietyId?: string
  stepCode: string
  equipmentId?: string
  minPct: string
  maxPct: string
  minFlushKg?: string
  minInputKg?: string
  effectiveFrom?: string
  changeNote?: string
}

const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/
const KG_RE = /^\d{1,8}(\.\d{1,2})?$/
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const validDay = (v: string) => ISO_DAY.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`))
const clean = (s: string) => s.trim().replace(/,/g, '')

/** PACK runs are judged on their loss limit only — no yield band (R28). */
export const BANDLESS_STEPS: ReadonlySet<string> = new Set(['PACK'])

export const emptyYieldBandDraft = (): YieldBandDraft => ({
  cropId: '',
  cropVarietyId: '',
  stepCode: '',
  equipmentId: '',
  minPct: '',
  maxPct: '',
  minFlushKg: '',
  minInputKg: '',
  effectiveFrom: '',
  changeNote: '',
})

/** "68.00" → "68", "68.50" → "68.5". */
export function trimDecimal(v: string | null | undefined): string {
  if (!v) return ''
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v
}

/** A new version of an existing band: same key and figures, a fresh date and note. */
export function draftFromBand(b: YieldBandKey & { minPct: string; maxPct: string; minFlushKg: string | null; minInputKg: string | null }): YieldBandDraft {
  return {
    cropId: b.cropId,
    cropVarietyId: b.cropVarietyId ?? '',
    stepCode: b.stepCode,
    equipmentId: b.equipmentId ?? '',
    minPct: trimDecimal(b.minPct),
    maxPct: trimDecimal(b.maxPct),
    minFlushKg: trimDecimal(b.minFlushKg),
    minInputKg: trimDecimal(b.minInputKg),
    effectiveFrom: '',
    changeNote: '',
  }
}

export const sameBandKey = (a: YieldBandKey, d: YieldBandDraft) =>
  a.cropId === d.cropId &&
  (a.cropVarietyId ?? '') === d.cropVarietyId &&
  a.stepCode === d.stepCode &&
  (a.equipmentId ?? '') === d.equipmentId

/** The newest version among `rows` with the draft's key (null: this saves version 1). */
export function previousBand<R extends YieldBandKey & PreviousBand>(rows: readonly R[], d: YieldBandDraft): R | null {
  let best: R | null = null
  for (const r of rows) if (sameBandKey(r, d) && (!best || r.version > best.version)) best = r
  return best
}

function pct(v: string): string | undefined {
  if (!v) return 'Required'
  if (!PCT_RE.test(v)) return 'A percent with up to 2 decimals, e.g. 68 or 68.5'
  if (Number(v) > 100) return 'At most 100'
  return undefined
}

export function validateYieldBandDraft(d: YieldBandDraft, prev: PreviousBand | null, today: string): YieldBandProblems {
  const p: YieldBandProblems = {}
  if (!d.cropId) p.cropId = 'Choose the crop'
  if (!d.stepCode) p.stepCode = 'Choose the step'
  else if (BANDLESS_STEPS.has(d.stepCode)) p.stepCode = 'Packing runs are judged on the loss limit only'
  const min = clean(d.minPct)
  const max = clean(d.maxPct)
  p.minPct = pct(min)
  p.maxPct = pct(max)
  if (!p.minPct && !p.maxPct && Number(min) > Number(max)) p.maxPct = 'Below the lowest yield'
  for (const k of ['minFlushKg', 'minInputKg'] as const) {
    const v = clean(d[k])
    if (v && !KG_RE.test(v)) p[k] = 'Kg with up to 2 decimals, e.g. 4 or 300.5'
  }
  const from = d.effectiveFrom || today
  if (d.effectiveFrom && !validDay(d.effectiveFrom)) p.effectiveFrom = 'Not a date'
  else if (prev && from < prev.effectiveFrom) p.effectiveFrom = `Not before version ${prev.version} (${prev.effectiveFrom})`
  const note = d.changeNote.trim()
  if (prev && !note) p.changeNote = 'Say why the band changes'
  else if (note.length > 200) p.changeNote = 'At most 200 characters'
  for (const k of Object.keys(p) as Array<keyof YieldBandDraft>) if (!p[k]) delete p[k]
  return p
}

export const yieldBandOk = (p: YieldBandProblems) => Object.values(p).every((v) => !v)

/** POST /yield-bands body: empty optional fields left out. Call only when valid. */
export function yieldBandBody(d: YieldBandDraft): YieldBandBody {
  const flush = clean(d.minFlushKg)
  const input = clean(d.minInputKg)
  const note = d.changeNote.trim()
  return {
    cropId: d.cropId,
    ...(d.cropVarietyId ? { cropVarietyId: d.cropVarietyId } : {}),
    stepCode: d.stepCode,
    ...(d.equipmentId ? { equipmentId: d.equipmentId } : {}),
    minPct: clean(d.minPct),
    maxPct: clean(d.maxPct),
    ...(flush ? { minFlushKg: flush } : {}),
    ...(input ? { minInputKg: input } : {}),
    ...(d.effectiveFrom ? { effectiveFrom: d.effectiveFrom } : {}),
    ...(note ? { changeNote: note } : {}),
  }
}
